import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import {
  Activity,
  BookOpen,
  Building2,
  ClipboardCheck,
  FileText,
  Fingerprint,
  Download,
  LayoutDashboard,
  MessagesSquare,
  ShieldCheck,
  Users,
  ArrowLeft,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import AccessibilityPanel from "@/components/AccessibilityPanel";
import ActAsBanner from "@/components/layout/ActAsBanner";

const sections = [
  { path: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { path: "/admin/customers", label: "Customers", icon: Building2 },
  { path: "/admin/verification", label: "Verification", icon: ClipboardCheck },
  { path: "/admin/leads", label: "Leads", icon: MessagesSquare },
  { path: "/admin/framework", label: "Framework", icon: BookOpen },
  { path: "/admin/content", label: "Content", icon: FileText },
  { path: "/admin/personas", label: "Personas", icon: ShieldCheck },
  { path: "/admin/persona-imports", label: "DI Voices", icon: Download },
  { path: "/admin/staff", label: "Staff & access", icon: Users },
  { path: "/admin/audit", label: "Audit", icon: Activity },
  { path: "/admin/sso", label: "SSO", icon: Fingerprint },
];

function ConsoleNavigation({ onNavigate, firstLinkRef }) {
  return (
    <nav aria-label="DI Console sections" className="flex-1 px-3 py-4">
      <ul className="space-y-1" role="list">
        {sections.map(({ path, label, icon: Icon, end }, index) => (
          <li key={path}>
            <NavLink
              ref={index === 0 ? firstLinkRef : undefined}
              to={path}
              end={end}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 ${isActive ? "bg-teal-700 text-white shadow-sm" : "text-white/70 hover:bg-white/10 hover:text-white"}`
              }
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function ConsoleUtilities({ onNavigate, onAccessibility, onLogout, accessibilityButtonRef }) {
  return (
    <div className="space-y-1 border-t border-white/10 px-3 py-3">
      <Link
        to="/"
        onClick={onNavigate}
        className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        <span>Return to application</span>
      </Link>
      <button
        ref={accessibilityButtonRef}
        type="button"
        onClick={(event) => { onNavigate?.(); onAccessibility(event.currentTarget); }}
        aria-label="Open accessibility settings"
        aria-haspopup="dialog"
        className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
      >
        Accessibility
      </button>
      <button
        type="button"
        onClick={() => { onNavigate?.(); onLogout(); }}
        className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        <span>Sign out</span>
      </button>
    </div>
  );
}

export default function AdminLayout() {
  const { logout } = useAuth();
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const desktopAccessibilityRef = useRef(null);
  const accessibilityReturnRef = useRef(null);
  const openAccessibility = (invoker) => {
    accessibilityReturnRef.current = invoker;
    setShowAccessibility(true);
  };
  const closeAccessibility = () => {
    const visible = (target) => target?.isConnected && target.getClientRects().length > 0;
    if (!visible(accessibilityReturnRef.current)) {
      accessibilityReturnRef.current = visible(desktopAccessibilityRef.current)
        ? desktopAccessibilityRef.current : menuButtonRef.current;
    }
    setShowAccessibility(false);
  };
  const firstMobileLinkRef = useRef(null);

  const closeMobileNavigation = () => setMobileNavigationOpen(false);

  useEffect(() => {
    if (!mobileNavigationOpen) return undefined;
    firstMobileLinkRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setMobileNavigationOpen(false);
      requestAnimationFrame(() => menuButtonRef.current?.focus());
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileNavigationOpen]);

  return (
    <div className="admin-console flex min-h-screen min-w-0 bg-background text-foreground">
      <aside
        className="navigation-focus-surface hidden w-64 shrink-0 flex-col bg-[#111111] text-white md:flex"
        aria-label="DI Console navigation"
      >
        <div className="flex h-16 items-center border-b border-white/10 px-6 py-3">
          <Link
            to="/"
            className="inline-flex rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
            aria-label="Diverse Integration home"
          >
            <img
              data-decorative-image="true"
              src="/diverse-integration-logo.png"
              alt="Diverse Integration"
              className="h-10 w-10 object-contain"
            />
            <span className="text-only-alternative px-2 font-semibold">Diverse Integration home</span>
          </Link>
        </div>
        <ConsoleNavigation />
        <ConsoleUtilities
          accessibilityButtonRef={desktopAccessibilityRef}
          onAccessibility={openAccessibility}
          onLogout={() => logout("/login")}
        />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <ActAsBanner />
        <header className="navigation-focus-surface border-b border-border bg-card" role="banner">
          <div className="flex min-h-16 w-full flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <button
              ref={menuButtonRef}
              type="button"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-border px-3 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 md:hidden"
              aria-expanded={mobileNavigationOpen}
              aria-controls="admin-mobile-navigation"
              aria-label={mobileNavigationOpen ? "Close console navigation" : "Open console navigation"}
              onClick={() => setMobileNavigationOpen((open) => !open)}
            >
              {mobileNavigationOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
              <span className="ml-2">Menu</span>
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
                Provider console
              </p>
              <h1 className="mt-1 break-words text-2xl font-bold text-foreground sm:text-3xl">
                Diverse Integration console
              </h1>
            </div>
          </div>
        </header>
        {mobileNavigationOpen && (
          <aside
            id="admin-mobile-navigation"
            className="navigation-focus-surface flex max-h-[calc(100vh-5rem)] flex-col overflow-y-auto border-b border-white/10 bg-[#111111] text-white md:hidden"
            aria-label="DI Console navigation"
          >
            <ConsoleNavigation onNavigate={closeMobileNavigation} firstLinkRef={firstMobileLinkRef} />
            <ConsoleUtilities
              onNavigate={closeMobileNavigation}
              onAccessibility={() => openAccessibility(menuButtonRef.current)}
              onLogout={() => logout("/login")}
            />
          </aside>
        )}
        <main
          id="main-content"
          tabIndex={-1}
          role="main"
          aria-label="DI Console"
          className="min-w-0 flex-1 overflow-y-auto bg-background px-4 py-6 sm:px-6 lg:px-8"
        >
          <div className="w-full max-w-[1440px]">
            <header className="mb-5 border-b border-border pb-5">
              <div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Operate customers, content, access, and support work from one
                  place.
                </p>
              </div>
            </header>
            <section aria-label="Console section" className="min-w-0">
              <Outlet />
            </section>
          </div>
        </main>
      </div>
      {showAccessibility && (
        <AccessibilityPanel returnFocusRef={accessibilityReturnRef} onClose={closeAccessibility} />
      )}
    </div>
  );
}
